import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { registerLogger } from '~~/helpers/logger';
import { getEnvironmental } from '~~/helpers/env/getEnvironmental';
import { isDevelopment } from '~~/helpers/env/checkEnvironmentType';
import { StringBoolean } from '~~/types/StringBoolean';
import { DATADOG_CLIENT_TOKEN } from '~~/constants/log';

// Browsers redact the real message/stack of errors thrown by cross-origin scripts that
// aren't served with a matching Access-Control-Allow-Origin (and loaded with crossorigin=
// anonymous), collapsing them all into this generic, undiagnosable placeholder. There is no
// useful signal to extract from it after the fact, so it's filtered out at the source rather
// than forwarded to Datadog.
//
// The browser hands window.onerror the raw string "Script error.", but the Datadog SDK
// re-formats non-Error values as `Uncaught ${JSON.stringify(originalError)}` before it ever
// reaches beforeSend - so the message actually seen here is `Uncaught "Script error."`. Match
// both the raw and SDK-wrapped forms rather than the raw string alone.
const GENERIC_SCRIPT_ERROR_PATTERN = /^(?:uncaught\s+)?"?script error\.?"?$/i;

const isGenericCrossOriginScriptError = (message: unknown): boolean =>
  typeof message === 'string' && GENERIC_SCRIPT_ERROR_PATTERN.test(message.trim());

export default () => {

  if (!isDevelopment()) {

    const appVersion = getEnvironmental('TAG');
    const datadogDisableFlag = getEnvironmental('DISABLE_DATADOG');
    const isDatadogDisabled = datadogDisableFlag === StringBoolean.true;
    const rumSessionSampleRate = isDatadogDisabled ? 0 : 5;
    const rumSessionReplaySampleRate = 0;
    const logsSessionSampleRate = isDatadogDisabled ? 0 : 100;

    datadogRum.init({
      applicationId: '8283985b-90ce-4af3-8ffa-b0e14d303e7d',
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.eu',
      service: 'vsf-client',
      env: window?.location?.host ?? 'nuxt',
      version: appVersion,
      sessionSampleRate: rumSessionSampleRate,
      sessionReplaySampleRate: rumSessionReplaySampleRate,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      silentMultipleInit: true,
      beforeSend: (event) => {

        if (event.type === 'error' && isGenericCrossOriginScriptError(event.error?.message)) {

          return false;

        }

        return true;

      },
    });

    datadogLogs.init({
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.eu',
      service: 'vsf-browser-logs',
      env: window?.location?.host ?? 'default-env',
      version: appVersion,
      forwardConsoleLogs: ['warn', 'error'],
      forwardErrorsToLogs: true,
      sessionSampleRate: logsSessionSampleRate,
      beforeSend: (log) => {

        if (isGenericCrossOriginScriptError(log.message)) {

          return false;

        }

        return true;

      },
    });

    datadogLogs.logger.info('Datadog resolved sample rates', {
      disableDatadog: isDatadogDisabled,
      rumSessionSampleRate,
      rumSessionReplaySampleRate,
      logsSessionSampleRate,
    });

    const buildContext = (value: unknown): Record<string, unknown> =>
      typeof value === 'object' && value !== null
        ? value as Record<string, unknown>
        : { value };

    const ddLogger = {
      debug: (...args: unknown[]) => datadogLogs.logger.debug(String(args[0]), buildContext(args[1])),
      info: (...args: unknown[]) => datadogLogs.logger.info(String(args[0]), buildContext(args[1])),
      warn: (...args: unknown[]) => datadogLogs.logger.warn(String(args[0]), buildContext(args[1])),
      error: (...args: unknown[]) => datadogLogs.logger.error(String(args[0]), buildContext(args[1])),
    };

    const logsVerbosity = 'info';

    registerLogger(ddLogger, logsVerbosity);

  }

};
